import { Inject, Injectable } from '@angular/core';

import { Actions, ofType, createEffect } from '@ngrx/effects';

import * as contactsAction from '../actions/contacts.action';
import * as fromRoot from '@usimweb/state/router-store';

import { ApiService } from '@usimweb/core/services';
import { catchError, map, of, switchMap } from 'rxjs';

import { Contacts, Env, PendingContacts } from '@usimweb/core/models';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable()
export class ContactsEffects {
  constructor(
    @Inject('environment') private environment: Env,
    private actions$: Actions,
    private apiService: ApiService,
    private snackBar: MatSnackBar
  ) {}

  private DEBUG_TIME: number = this.environment.production === true ? 0 : 1000;
  $loadContacts = createEffect(() => {
    return this.actions$.pipe(
      ofType(contactsAction.LOAD_CONTACTS),
      switchMap(() => {
        return this.apiService.getContacts().pipe(
          map((contacts: Contacts[]) => new contactsAction.LoadContactsSuccess(contacts)),
          catchError((error) => of(new contactsAction.LoadContactsSuccess(error)))
        );
      })
    );
  });

  $loadProjectContacts = createEffect(() => {
    return this.actions$.pipe(
      ofType(contactsAction.LOAD_PROJECT_CONTACTS),
      map((action: contactsAction.LoadProjectContacts) => action.payload),
      switchMap((uuid) => {
        return this.apiService.getProjectContacts(uuid).pipe(
          map((contacts: Contacts[]) => new contactsAction.LoadProjectContactsSuccess(contacts)),
          catchError((error) => of(new contactsAction.LoadProjectContactsFail(error)))
        );
      })
    );
  });

  $loadRequests = createEffect(() => {
    return this.actions$.pipe(
      ofType(contactsAction.LOAD_REQUESTS),
      switchMap(() => {
        return this.apiService.getContactRequests().pipe(
          map((contacts: Contacts[]) => new contactsAction.LoadRequestsSuccess(contacts)),
          catchError((error) => of(new contactsAction.LoadRequestsFail(error)))
        );
      })
    );
  });

  $loadRequest = createEffect(() => {
    return this.actions$.pipe(
      ofType(contactsAction.LOAD_REQUEST),
      map((action: contactsAction.LoadRequest) => action.payload),
      switchMap((id) => {
        return this.apiService.getContactRequest(id).pipe(
          map((contacts: Contacts[]) => new contactsAction.LoadRequestSuccess(contacts)),
          catchError((error) => of(new contactsAction.LoadRequestFail(error)))
        );
      })
    );
  });

  $pendingRequests = createEffect(() => {
    return this.actions$.pipe(
      ofType(contactsAction.LOAD_PENDING_REQUESTS),
      switchMap(() => {
        return this.apiService.pendingRequests().pipe(
          map((contacts: PendingContacts[]) => new contactsAction.LoadPendingRequestsSuccess(contacts)),
          catchError((error) => of(new contactsAction.LoadPendingRequestsFail(error)))
        );
      })
    );
  });

  $deleteProject = createEffect(() => {
    return this.actions$.pipe(
      ofType(contactsAction.DELETE_CONTACTS),
      map((action: contactsAction.DeleteContacts) => action.payload),
      switchMap((username: string) => {
        return this.apiService.deleteContact(username).pipe(
          map(() => {
            this.snackBar.open('Contact deleted...', 'OK', { duration: 2000 });
            return new contactsAction.DeleteContactsSuccess(username);
          }),
          catchError((error) => of(new contactsAction.DeleteContactsFail(error)))
        );
      })
    );
  });

  $sendContactRequest = createEffect(() => {
    return this.actions$.pipe(
      ofType(contactsAction.CREATE_REQUEST),
      map((action: contactsAction.CreateRequest) => action.payload),
      switchMap((mail: string) => {
        return this.apiService.sendContactRequest(mail).pipe(
          map(() => {
            this.snackBar.open('Request send to ' + mail + '...', 'OK', { duration: 2000 });
            return new contactsAction.CreateRequestsSuccess();
          }),
          catchError((error) => {
            console.log(error.error);
            if (error.status === 400) {
              const errorDetail = error.error?.[0] || error.error.detail || 'Bad request';
              const errorMessages = error.error.messages || {};
              let errorMessage = errorDetail;
              // Check if there are any field-specific error messages
              if (Object.keys(errorMessages).length > 0) {
                const fieldErrors = Object.keys(errorMessages).map((field) => {
                  const fieldMessage = errorMessages[field][0];
                  return `${field}: ${fieldMessage}`;
                });
                errorMessage += '\n' + fieldErrors.join('\n');
              }
              this.snackBar.open(`Bad request (400): ${errorMessage}`, 'OK', { duration: 5000 });
            } else if (error.status === 500) {
              const errorDetail = error.error.detail || 'Internal server error';
              this.snackBar.open(`Internal server error (500): ${errorDetail}`, 'OK', { duration: 5000 });
            } else {
              this.snackBar.open('Some error occurred... Please contact the support team', 'OK', { duration: 5000 });
            }

            return of(new contactsAction.CreateRequestFail(error));
          })
        );
      })
    );
  });

  $acceptInvite = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(contactsAction.ACCEPT_INVITE),
        map((action: contactsAction.AcceptInvite) => action.payload),
        switchMap((id: number) => {
          return this.apiService.acceptInvite(id).pipe(
            map(() => {
              this.snackBar.open('Sharing permitted...', 'OK', {
                duration: 2000,
              });
              return new contactsAction.AcceptInviteSuccess();
            }),
            catchError((error) => of(new contactsAction.AcceptInviteFail(error)))
          );
        })
      );
    },
    { dispatch: false }
  );

  $rejectInvite = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(contactsAction.REJECT_INVITE),
        map((action: contactsAction.RejectInvite) => action.payload),
        switchMap((id: number) => {
          return this.apiService.rejectInvite(id).pipe(
            map(() => {
              this.snackBar.open('Sharing denied..', 'OK', { duration: 3000 });
              return new contactsAction.RejectInviteSuccess();
            }),
            catchError((error) => of(new contactsAction.RejectInviteFail(error)))
          );
        })
      );
    },
    { dispatch: false }
  );

  $backProjects = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(contactsAction.ACCEPT_INVITE_SUCCES || contactsAction.REJECT_INVITE_SUCCES),
        map((path) => {
          return new fromRoot.Go({
            path: ['app/projects'],
          });
        })
      );
    },
    { dispatch: false }
  );
}
